import { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ReschedulePropose, RescheduleDecision } from "@/components/RescheduleWizard";
import MatchTimeline from "@/components/MatchTimeline";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CalendarClock, Plus, Save, X as XIcon, Sparkles, Coins, Radio, Target, Trophy, Check, FileText } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import Countdown from "@/components/Countdown";

const HLL_MAPS = [
  "Stalingrad", "Kursk", "Kharkov", "El Alamein", "Foy", "Hurtgen Forest",
  "Utah Beach", "Omaha Beach", "Sainte-Mère-Église", "Sainte-Marie-du-Mont",
  "Carentan", "Purple Heart Lane", "Hill 400", "Driel", "Remagen",
  "Mortain", "Elsenborn Ridge", "Wheelspin",
];

function groupByDay(fixtures) {
  const groups = new Map();
  for (const f of fixtures) {
    const day = f.scheduled_at ? new Date(f.scheduled_at).toDateString() : "TBD";
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push(f);
  }
  return [...groups.entries()].sort(([a], [b]) => new Date(a) - new Date(b));
}

// Iter68 · Group by round number instead of calendar day. Within each round,
// fixtures are chronological; `TBD`/unassigned rounds bucket to the end so
// they don't crowd the top.
function groupByRound(fixtures) {
  const groups = new Map();
  for (const f of fixtures) {
    const key = f.round_number ?? "TBD";
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key).push(f);
  }
  const sorted = [...groups.entries()].sort(([a], [b]) => {
    if (a === "TBD") return 1;
    if (b === "TBD") return -1;
    return Number(a) - Number(b);
  });
  for (const [, list] of sorted) {
    list.sort((x, y) => (x.scheduled_at || "").localeCompare(y.scheduled_at || ""));
  }
  return sorted;
}

export default function Schedule() {
  // Iter63 · PDF download uses direct <a href>; api client's baseURL is the same.
  const API = api.defaults?.baseURL || "";
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const isOfficial = user?.role === "official" || isAdmin;

  const [teams, setTeams] = useState([]);
  const [myTeams, setMyTeams] = useState([]);
  const [fixtures, setFixtures] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [divFilter, setDivFilter] = useState("all"); // all | none | <division_id>
  const [groupMode, setGroupMode] = useState(() => localStorage.getItem("schedule.groupMode") || "day"); // "day" | "round"
  const [editRow, setEditRow] = useState(null);
  const [edit, setEdit] = useState({ scheduled_at: "", map: "", faction_home: "allies", faction_away: "axis" });
  const [addOpen, setAddOpen] = useState(false);
  const [add, setAdd] = useState({ home_team_id: "", away_team_id: "", scheduled_at: "", map: "Wheelspin", game: "Hell Let Loose", division_id: "" });
  const [seasonOpen, setSeasonOpen] = useState(false);
  const [season, setSeason] = useState({
    starts_at: "",
    round_gap_days: 7,
    default_map: "Wheelspin",
    home_away: true,
    announce_discord: true,
    division_ids: [],
    start_from_round: 1, // Iter61 · skip already-set rounds
  });
  const [tossFor, setTossFor] = useState(null);
  const [tossFaction, setTossFaction] = useState("allies");
  const [myDossier, setMyDossier] = useState(null);
  // Predict + score-submit (iter28)
  const [predictFor, setPredictFor] = useState(null);
  const [ph, setPh] = useState(1);
  const [pa, setPa] = useState(1);
  const [myPredictions, setMyPredictions] = useState({});
  const [scoreFor, setScoreFor] = useState(null);
  const [sh, setSh] = useState(0);
  const [sa, setSa] = useState(0);
  const [pullMode, setPullMode] = useState("off");
  const [rconLink, setRconLink] = useState("");
  const [rconCsv, setRconCsv] = useState("");
  // Iter95 · hide completed matches from the public schedule by default;
  // admins/officials can flip "Include completed" on to see historical rows.
  const [includeCompleted, setIncludeCompleted] = useState(false);

  const load = () => {
    api.get("/teams").then((r) => setTeams(r.data));
    api.get("/divisions").then((r) => setDivisions(r.data)).catch(() => {});
    api
      .get("/fixtures", { params: { include_completed: isOfficial && includeCompleted } })
      .then((r) => {
        setFixtures(r.data);
        if (user) {
          Promise.all(
            r.data
              .filter((f) => f.status === "upcoming" || !f.status)
              .map((f) => api.get(`/fixtures/${f.id}/predict/mine`).then((rr) => [f.id, rr.data]).catch(() => [f.id, null]))
          ).then((entries) => setMyPredictions(Object.fromEntries(entries)));
        }
      });
    if (user) {
      api.get("/me/teams").then((r) => setMyTeams(r.data)).catch(() => {});
      api.get("/me/streamer").then((r) => setMyDossier(r.data)).catch(() => setMyDossier(null));
    }
  };
  useEffect(() => { load(); }, [user]);
  // Iter95 · reload when the admin flips "Include completed".
  useEffect(() => { if (user) load(); /* eslint-disable-next-line */ }, [includeCompleted]);

  const myIds = useMemo(() => new Set(myTeams.map((t) => t.id)), [myTeams]);
  const teamById = (id) => teams.find((t) => t.id === id);
  const canEditFixture = (f) => isOfficial || myIds.has(f.home_team_id) || myIds.has(f.away_team_id);
  const mySide = (f) => myIds.has(f.home_team_id) ? "home" : (myIds.has(f.away_team_id) ? "away" : null);

  const startEdit = (f) => {
    setEditRow(f.id);
    setEdit({
      scheduled_at: f.scheduled_at ? new Date(f.scheduled_at).toISOString().slice(0, 16) : "",
      map: f.map || "",
      faction_home: f.faction_home || "allies",
      faction_away: f.faction_away || "axis",
      home_team_id: f.home_team_id || "",
      away_team_id: f.away_team_id || "",
      status: f.status || "upcoming",
    });
  };

  const saveEdit = async (f) => {
    const side = mySide(f);
    const body = {};
    if (isOfficial) {
      if (edit.scheduled_at) body.scheduled_at = new Date(edit.scheduled_at).toISOString();
      if (edit.map) body.map = edit.map;
      body.faction_home = edit.faction_home;
      body.faction_away = edit.faction_away;
      if (edit.home_team_id && edit.home_team_id !== f.home_team_id) body.home_team_id = edit.home_team_id;
      if (edit.away_team_id && edit.away_team_id !== f.away_team_id) body.away_team_id = edit.away_team_id;
      if (edit.status && edit.status !== f.status) body.status = edit.status;
    } else {
      // Reps: their own time change + only their side's faction
      if (edit.scheduled_at) body.scheduled_at = new Date(edit.scheduled_at).toISOString();
      if (side === "home") body.faction_home = edit.faction_home;
      if (side === "away") body.faction_away = edit.faction_away;
    }
    try {
      await api.put(`/fixtures/${f.id}`, body);
      toast.success("Fixture updated.");
      setEditRow(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const deleteFixture = async (f) => {
    const label = `${teamById(f.home_team_id)?.tag || "TBD"} vs ${teamById(f.away_team_id)?.tag || "TBD"}`;
    if (!window.confirm(`Delete fixture ${label}? Predictions and chat will be wiped too.`)) return;
    try {
      await api.delete(`/fixtures/${f.id}`);
      toast.success(`Fixture deleted — ${label}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const isVerifiedStreamer = !!(myDossier && myDossier.verified);

  const submitPrediction = async () => {
    if (!predictFor) return;
    try {
      await api.post(`/fixtures/${predictFor.id}/predict`, { home_score: Number(ph), away_score: Number(pa) });
      toast.success("Prediction locked.");
      setMyPredictions((m) => ({ ...m, [predictFor.id]: { home_score: Number(ph), away_score: Number(pa) } }));
      setPredictFor(null);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openScore = (f) => {
    setScoreFor(f);
    setSh(f.home_score ?? 0);
    setSa(f.away_score ?? 0);
    setPullMode("off");
    setRconLink("");
    setRconCsv("");
  };

  const submitScore = async () => {
    if (!scoreFor) return;
    if (Number(sh) < 0 || Number(sa) < 0) { toast.error("Scores must be positive."); return; }
    try {
      const payload = { home_score: Number(sh), away_score: Number(sa) };
      if (pullMode !== "off") {
        payload.pull_server_stats = pullMode;
        if (pullMode === "csv") payload.server_stats_csv = rconCsv;
      }
      const { data } = await api.post(`/fixtures/${scoreFor.id}/result`, payload);
      if (data?.status === "pending_signatures") {
        toast.success(`Score sheet signed — waiting on ${data.waiting_on?.toUpperCase()} rep to countersign.`);
      } else if (data?.status === "signature_mismatch") {
        toast.error("The other rep signed a different score. File a dispute from the Reps toolkit to escalate.");
      } else {
        const imp = data?.stats_import;
        if (imp) {
          const note = imp.fallback ? ` · ${imp.fallback}` : "";
          toast.success(`Result locked — pulled ${imp.updated_count} player stats (${imp.source})${note}.`);
        } else {
          toast.success("Result locked — standings auto-updated.");
        }
      }
      setScoreFor(null); setPullMode("off"); setRconLink(""); setRconCsv("");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const signupToCast = async (f) => {
    try {
      await api.post(`/fixtures/${f.id}/streamer-signup`);
      toast.success(`Signed up to cast — locked to ${f.scheduled_at ? new Date(f.scheduled_at).toLocaleString() : "kickoff time"}`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const unsignupFromCast = async (f) => {
    try {
      await api.delete(`/fixtures/${f.id}/streamer-signup`);
      toast.success("Cast signup removed.");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  // Iter36 · Reps hit "Ready in game" straight from the public fixture wall.
  // Backend auto-flips the coin when both sides ready.
  const markReadyFixture = async (f, side) => {
    try {
      const { data } = await api.post(`/fixtures/${f.id}/ready`, null, { params: side ? { side } : {} });
      if (data?.coin_toss?.auto) {
        const winnerTag = data.coin_toss.winner === "home"
          ? teamById(f.home_team_id)?.tag
          : teamById(f.away_team_id)?.tag;
        toast.success(`Both ready — coin flipped, ${winnerTag} won the toss!`);
      } else {
        toast.success("Marked ready.");
      }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const callCoinToss = async (f) => {
    try {
      const { data } = await api.post(`/fixtures/${f.id}/coin-toss`);
      const winnerTag = data.coin_toss?.winner === "home" ? teamById(f.home_team_id)?.tag : teamById(f.away_team_id)?.tag;
      toast.success(data.already ? `Toss already called — ${winnerTag} won.` : `${winnerTag} won the toss!`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const submitFaction = async () => {
    if (!tossFor) return;
    try {
      await api.post(`/fixtures/${tossFor.id}/coin-toss/faction`, null, { params: { faction: tossFaction } });
      toast.success(`Locked ${tossFaction.toUpperCase()}.`);
      setTossFor(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const generateSeason = async () => {
    if (!season.starts_at) { toast.error("First kickoff is required."); return; }
    if (!season.division_ids?.length) { toast.error("Pick at least one division."); return; }
    try {
      const { data } = await api.post("/admin/season/round-robin", {
        starts_at: new Date(season.starts_at).toISOString(),
        round_gap_days: Number(season.round_gap_days) || 7,
        default_map: season.default_map || "Wheelspin",
        home_away: !!season.home_away,
        announce_discord: !!season.announce_discord,
        division_ids: season.division_ids,
        start_from_round: Math.max(1, Number(season.start_from_round) || 1),
      });
      const skipped = data.skipped?.length ? ` · Skipped ${data.skipped.length} div${data.skipped.length === 1 ? "" : "s"} (too few teams)` : "";
      const dis = data.discord?.team_channels
        ? ` · Discord posted to ${data.discord.team_channels} team channel${data.discord.team_channels === 1 ? "" : "s"}`
        : data.discord?.fallback_posts
          ? ` · Announced via webhook`
          : "";
      toast.success(`Season drawn — ${data.created} fixtures across ${data.divisions} division${data.divisions === 1 ? "" : "s"}.${skipped}${dis}`);
      setSeasonOpen(false);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Failed";
      toast.error(msg);
    }
  };

  const toggleSeasonDivision = (id) => {
    setSeason((s) => {
      const has = s.division_ids.includes(id);
      return { ...s, division_ids: has ? s.division_ids.filter((x) => x !== id) : [...s.division_ids, id] };
    });
  };

  const seasonRoundsPreview = useMemo(() => {
    if (!season.division_ids?.length) return [];
    const skip = Math.max(0, (Number(season.start_from_round) || 1) - 1);
    return season.division_ids.map((id) => {
      const div = divisions.find((d) => d.id === id);
      const pool = teams.filter((t) => t.division_id === id && (t.status === "approved" || !t.status));
      const n = pool.length;
      if (n < 2) return { id, name: div?.name || id, teams: n, rounds: 0, matches: 0, warn: "need ≥2 teams" };
      const hasBye = n % 2 === 1;
      const roundsPerHalf = hasBye ? n : n - 1;
      const matchesPerRound = Math.floor(n / 2);
      const fullRounds = season.home_away ? roundsPerHalf * 2 : roundsPerHalf;
      const rounds = Math.max(0, fullRounds - skip);
      if (rounds === 0) {
        return { id, name: div?.name || id, teams: n, rounds: 0, matches: 0, warn: `skip>${fullRounds} rounds` };
      }
      const totalMatches = matchesPerRound * rounds;
      return {
        id, name: div?.name || id, teams: n,
        rounds,
        matches: totalMatches,
        hasBye,
        skip,
      };
    });
  }, [season.division_ids, season.home_away, season.start_from_round, divisions, teams]);

  // Iter62 · Auto-detect the next available round across the selected divisions
  // so admin doesn't have to count. Uses the currently-loaded fixtures list.
  // Returns 0 when no existing fixtures for those divisions — meaning
  // "generate from R1, no skip needed".
  const suggestedStartFrom = useMemo(() => {
    if (!season.division_ids?.length) return 0;
    const rounds = fixtures
      .filter((f) => season.division_ids.includes(f.division_id) && typeof f.round_number === "number")
      .map((f) => f.round_number);
    if (rounds.length === 0) return 0;
    return Math.max(...rounds) + 1;
  }, [season.division_ids, fixtures]);

  const addFixture = async () => {
    if (!add.home_team_id || !add.away_team_id || !add.scheduled_at) { toast.error("Home, away & date required."); return; }
    if (add.home_team_id === add.away_team_id) { toast.error("Home and away must be different."); return; }
    try {
      const body = {
        home_team_id: add.home_team_id,
        away_team_id: add.away_team_id,
        scheduled_at: new Date(add.scheduled_at).toISOString(),
        map: add.map,
        game: add.game,
      };
      if (add.division_id) body.division_id = add.division_id;
      await api.post("/fixtures", body);
      toast.success("Fixture created.");
      setAddOpen(false);
      setAdd({ home_team_id: "", away_team_id: "", scheduled_at: "", map: "Wheelspin", game: "Hell Let Loose", division_id: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (user === null) return <div className="text-gray-500 font-mono text-xs uppercase tracking-widest">Loading…</div>;
  if (user === false) return <Navigate to="/login" replace />;
  const filteredFixtures = divFilter === "all"
    ? fixtures
    : divFilter === "none"
      ? fixtures.filter((f) => !f.division_id)
      : fixtures.filter((f) => f.division_id === divFilter);
  const groups = groupMode === "round"
    ? groupByRound(filteredFixtures)
    : groupByDay(filteredFixtures);
  const divisionById = (id) => divisions.find((d) => d.id === id);

  return (
    <div className="space-y-8" data-testid="schedule-root">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-cyan-400 font-mono tracking-[0.3em] text-xs mb-2 flex items-center gap-2">
            <CalendarClock className="w-3.5 h-3.5" /> / SEASON SCHEDULE
          </div>
          <h1 className="font-heading text-4xl lg:text-5xl font-black tracking-tighter uppercase">Schedule</h1>
          <p className="text-gray-400 mt-2 text-sm max-w-xl">
            Every match on the wall. Reps can nudge their times and pick a faction; officials & admins run the full board.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={() => setSeasonOpen(true)}
              data-testid="season-generate-open"
              className="rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Season generator
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="schedule-add-open" className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold">
              <Plus className="w-4 h-4 mr-2" /> Add fixture
            </Button>
            {divFilter !== "all" && divFilter !== "none" && (
              <a
                href={`${API}/schedule/${divFilter}/pdf`}
                target="_blank"
                rel="noreferrer"
                data-testid="schedule-pdf-download"
                title="Download the locked team-vs-team slate as PDF for this division (no dates/times — reference only)"
                className="rounded-none bg-black border-2 border-cyan-400 text-cyan-400 hover:bg-cyan-400 hover:text-black uppercase tracking-widest font-bold text-sm px-4 py-2 inline-flex items-center transition-colors"
              >
                <FileText className="w-4 h-4 mr-2" /> Download PDF
              </a>
            )}
          </div>
        )}
      </div>

      {/* Division filter pills */}
      {divisions.length > 0 && (
        <div className="flex flex-wrap gap-2 items-center" data-testid="schedule-division-filter">
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Filter:</span>
          <button
            onClick={() => setDivFilter("all")}
            data-testid="division-filter-all"
            className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${
              divFilter === "all" ? "bg-cyan-400 text-black border-cyan-400" : "text-gray-400 border-[#232330] hover:border-cyan-400"
            }`}
          >
            All ({fixtures.length})
          </button>
          {divisions.map((d) => {
            const count = fixtures.filter((f) => f.division_id === d.id).length;
            return (
              <button
                key={d.id}
                onClick={() => setDivFilter(d.id)}
                data-testid={`division-filter-${d.id}`}
                className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${
                  divFilter === d.id ? "text-black border-transparent" : "text-gray-400 border-[#232330] hover:text-white"
                }`}
                style={divFilter === d.id ? { background: d.color || "#F4D03F", borderColor: d.color || "#F4D03F" } : { borderLeftColor: d.color || "#232330", borderLeftWidth: 3 }}
              >
                {d.name} ({count})
              </button>
            );
          })}
          <button
            onClick={() => setDivFilter("none")}
            data-testid="division-filter-none"
            className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 border transition-colors ${
              divFilter === "none" ? "bg-gray-400 text-black border-gray-400" : "text-gray-500 border-[#232330] hover:border-gray-400"
            }`}
          >
            Unassigned ({fixtures.filter((f) => !f.division_id).length})
          </button>
          {/* Iter68 · Group-by toggle · remembered via localStorage */}
          <span className="mx-2 h-4 w-px bg-[#232330]" aria-hidden />
          <span className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Group:</span>
          <div className="inline-flex border border-[#232330]">
            <button
              onClick={() => { setGroupMode("day"); localStorage.setItem("schedule.groupMode", "day"); }}
              data-testid="schedule-group-day"
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 transition-colors ${
                groupMode === "day" ? "bg-[#F4D03F] text-black" : "text-gray-400 hover:text-white"
              }`}
            >
              By date
            </button>
            <button
              onClick={() => { setGroupMode("round"); localStorage.setItem("schedule.groupMode", "round"); }}
              data-testid="schedule-group-round"
              className={`text-[10px] font-mono uppercase tracking-widest px-2.5 py-1 transition-colors border-l border-[#232330] ${
                groupMode === "round" ? "bg-[#F4D03F] text-black" : "text-gray-400 hover:text-white"
              }`}
            >
              By round
            </button>
          </div>
          {/* Iter95 · Admins can include completed matches in the public schedule view. */}
          {isOfficial && (
            <>
              <span className="mx-2 h-4 w-px bg-[#232330]" aria-hidden />
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="schedule-include-completed-row">
                <input
                  type="checkbox"
                  checked={includeCompleted}
                  onChange={(e) => setIncludeCompleted(e.target.checked)}
                  data-testid="schedule-include-completed"
                  className="accent-[#F4D03F]"
                />
                <span className="text-[10px] font-mono uppercase tracking-widest text-gray-400">
                  Include completed
                </span>
              </label>
            </>
          )}
        </div>
      )}

      {addOpen && (
        <div className="border border-[#F4D03F]/60 bg-[#101015] p-4 grid gap-2 sm:grid-cols-6 items-end">
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Home</Label>
            <select
              value={add.home_team_id}
              onChange={(e) => setAdd({ ...add, home_team_id: e.target.value })}
              className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              data-testid="add-home"
            >
              <option value="">— pick —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Away</Label>
            <select
              value={add.away_team_id}
              onChange={(e) => setAdd({ ...add, away_team_id: e.target.value })}
              className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              data-testid="add-away"
            >
              <option value="">— pick —</option>
              {teams.map((t) => <option key={t.id} value={t.id}>{t.tag}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Division</Label>
            <select
              value={add.division_id}
              onChange={(e) => setAdd({ ...add, division_id: e.target.value })}
              className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              data-testid="add-division"
            >
              <option value="">— none —</option>
              {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Kickoff (UTC)</Label>
            <Input
              data-testid="add-when"
              type="datetime-local"
              value={add.scheduled_at}
              onChange={(e) => setAdd({ ...add, scheduled_at: e.target.value })}
              className="rounded-none bg-[#05050A] border-[#232330] mt-1 font-mono text-xs"
            />
          </div>
          <div>
            <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">Map</Label>
            <select
              value={add.map}
              onChange={(e) => setAdd({ ...add, map: e.target.value })}
              className="w-full rounded-none bg-[#05050A] border border-[#232330] mt-1 p-2 text-sm"
              data-testid="add-map"
            >
              {HLL_MAPS.map((m) => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={addFixture}
              data-testid="schedule-add-submit"
              className="rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest text-xs font-bold"
            >
              <Save className="w-3.5 h-3.5 mr-1" /> Create
            </Button>
            <Button
              onClick={() => setAddOpen(false)}
              variant="outline"
              className="rounded-none border-[#232330] hover:border-red-500 hover:text-red-500 uppercase tracking-widest text-xs"
            >
              <XIcon className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {groups.map(([key, list]) => {
          const label = groupMode === "round"
            ? (key === "TBD" ? "Unassigned round" : `Round ${key}`)
            : (key === "TBD" ? "Undated" : key);
          const slug = String(key).replace(/\s+/g, "-");
          const testId = groupMode === "round" ? `schedule-round-${slug}` : `schedule-day-${slug}`;
          return (
          <section key={`${groupMode}-${key}`} data-testid={testId} className="space-y-2">
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] border-b border-[#232330] pb-2 flex items-center justify-between">
              <span>{label}</span>
              {groupMode === "round" && (
                <span className="text-gray-500 normal-case">
                  {list.length} {list.length === 1 ? "fixture" : "fixtures"}
                </span>
              )}
            </div>
            {list.map((f) => {
              const h = teamById(f.home_team_id), a = teamById(f.away_team_id);
              const editing = editRow === f.id;
              const side = mySide(f);
              return (
                <div
                  key={f.id}
                  data-testid={`schedule-row-${f.id}`}
                  className="border border-[#232330] bg-[#101015] p-4 grid gap-3 lg:grid-cols-[1.4fr_1fr_1fr_auto] items-center"
                >
                  {side && f.reschedule_proposal?.status === "pending" && (
                    <div className="lg:col-span-4">
                      <RescheduleDecision fixture={f} onDone={load} />
                    </div>
                  )}
                  <div>
                    <div className="font-heading font-bold uppercase tracking-tight">
                      <span style={{ color: h?.logo_color }}>{h?.tag || "TBD"}</span>{" "}
                      <span className="text-gray-500">vs</span>{" "}
                      <span style={{ color: a?.logo_color }}>{a?.tag || "TBD"}</span>
                    </div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                      {f.map || "TBD map"}{f.wheelspin_map ? ` · rolled: ${f.wheelspin_map}` : ""} · {f.status}
                    </div>
                    <div className="mt-2">
                      <MatchTimeline fixture={f} size="sm" />
                    </div>
                  </div>

                  {editing ? (
                    <Input
                      data-testid={`edit-when-${f.id}`}
                      type="datetime-local"
                      value={edit.scheduled_at}
                      onChange={(e) => setEdit({ ...edit, scheduled_at: e.target.value })}
                      className="rounded-none bg-[#05050A] border-[#232330] font-mono text-xs"
                    />
                  ) : (
                    <div className="md:col-span-2 text-sm text-white font-mono">{f.scheduled_at ? new Date(f.scheduled_at).toLocaleString() : "TBD"}</div>
                  )}

                  {editing ? (
                    <div className="flex gap-2 text-[10px] font-mono flex-wrap">
                      {(isOfficial || side === "home") && (
                        <select
                          value={edit.faction_home}
                          onChange={(e) => setEdit({ ...edit, faction_home: e.target.value })}
                          data-testid={`edit-fh-${f.id}`}
                          className="rounded-none bg-[#05050A] border border-cyan-400/40 p-1 uppercase"
                        >
                          <option value="allies">{h?.tag} allies</option>
                          <option value="axis">{h?.tag} axis</option>
                        </select>
                      )}
                      {(isOfficial || side === "away") && (
                        <select
                          value={edit.faction_away}
                          onChange={(e) => setEdit({ ...edit, faction_away: e.target.value })}
                          data-testid={`edit-fa-${f.id}`}
                          className="rounded-none bg-[#05050A] border border-[#FF007F]/40 p-1 uppercase"
                        >
                          <option value="allies">{a?.tag} allies</option>
                          <option value="axis">{a?.tag} axis</option>
                        </select>
                      )}
                      {isOfficial && (
                        <>
                          <select
                            value={edit.home_team_id || ""}
                            onChange={(e) => setEdit({ ...edit, home_team_id: e.target.value })}
                            data-testid={`edit-home-${f.id}`}
                            className="rounded-none bg-[#05050A] border border-[#F4D03F]/40 p-1 uppercase max-w-[110px] truncate"
                            title="Swap home team"
                          >
                            {teams.map((tt) => <option key={tt.id} value={tt.id}>H · {tt.tag}</option>)}
                          </select>
                          <select
                            value={edit.away_team_id || ""}
                            onChange={(e) => setEdit({ ...edit, away_team_id: e.target.value })}
                            data-testid={`edit-away-${f.id}`}
                            className="rounded-none bg-[#05050A] border border-[#F4D03F]/40 p-1 uppercase max-w-[110px] truncate"
                            title="Swap away team"
                          >
                            {teams.map((tt) => <option key={tt.id} value={tt.id}>A · {tt.tag}</option>)}
                          </select>
                          <select
                            value={edit.status}
                            onChange={(e) => setEdit({ ...edit, status: e.target.value })}
                            data-testid={`edit-status-${f.id}`}
                            className="rounded-none bg-[#05050A] border border-[#B084FF]/40 p-1 uppercase"
                          >
                            <option value="upcoming">upcoming</option>
                            <option value="live">live</option>
                            <option value="completed">completed</option>
                            <option value="postponed">postponed</option>
                            <option value="cancelled">cancelled</option>
                          </select>
                        </>
                      )}
                    </div>
                  ) : (
                    <div className="md:col-span-3 text-[10px] font-mono uppercase tracking-widest">
                      <span className={`px-1.5 py-0.5 border mr-1 ${f.faction_home === "axis" ? "border-red-500 text-red-400" : "border-cyan-400 text-cyan-400"}`}>
                        {h?.tag} · {(f.faction_home || "allies").toUpperCase()}
                      </span>
                      <span className={`px-1.5 py-0.5 border ${f.faction_away === "axis" ? "border-red-500 text-red-400" : "border-cyan-400 text-cyan-400"}`}>
                        {a?.tag} · {(f.faction_away || "axis").toUpperCase()}
                      </span>
                    </div>
                  )}

                  <div className="md:col-span-4 flex gap-2 justify-end flex-wrap">
                    {/* Iter36 · Reps: Ready in game (auto coin-flip when both sides ready) */}
                    {side && f.status === "upcoming" && (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() => markReadyFixture(f, side)}
                          disabled={!!f.ready?.[side]}
                          data-testid={`schedule-ready-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 px-3 py-1.5 flex items-center gap-1 font-bold disabled:opacity-40 disabled:cursor-not-allowed"
                          title="Ready your side — when both teams ready, the coin flips automatically"
                        >
                          <Check className="w-3 h-3" /> {f.ready?.[side] ? "Ready ✓" : "Ready in game"}
                        </button>
                        {(f.ready?.home || f.ready?.away) && (
                          <span
                            data-testid={`schedule-ready-status-${f.id}`}
                            className="text-[9px] font-mono uppercase tracking-widest text-gray-500"
                          >
                            {f.ready?.home ? `${h?.tag} ✓` : `${h?.tag} …`} · {f.ready?.away ? `${a?.tag} ✓` : `${a?.tag} …`}
                          </span>
                        )}
                      </div>
                    )}
                    {user && f.status === "upcoming" && (
                      <div className="flex flex-col items-end gap-1">
                        <button
                          onClick={() => {
                            const mp = myPredictions[f.id];
                            setPredictFor(f);
                            setPh(mp?.home_score ?? 1);
                            setPa(mp?.away_score ?? 1);
                          }}
                          data-testid={`schedule-predict-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 px-3 py-1.5 flex items-center gap-1"
                        >
                          <Target className="w-3 h-3" /> {myPredictions[f.id] ? "Edit pick" : "Predict"}
                        </button>
                        {f.scheduled_at && (
                          <div data-testid={`schedule-countdown-${f.id}`} className="text-[9px] font-mono tracking-widest text-[#F4D03F] uppercase">
                            <Countdown iso={f.scheduled_at} size="sm" liveLabel="LIVE — LOCKED" />
                          </div>
                        )}
                      </div>
                    )}
                    {/* Iter51 · Reschedule wizard — reps only, upcoming fixtures only */}
                    {side && f.status === "upcoming" && (
                      <ReschedulePropose fixture={f} onDone={load} />
                    )}
                    {/* Iter30: Submit result moved to Reps Toolkit. */}
                    {isVerifiedStreamer && (() => {
                      const sig = (f.streamer_signups || []).find((s) => s.streamer_id === myDossier?.id);
                      if (sig) {
                        return (
                          <button
                            onClick={() => unsignupFromCast(f)}
                            data-testid={`schedule-cast-unsignup-${f.id}`}
                            className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] bg-[#B084FF]/10 hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1"
                          >
                            <Radio className="w-3 h-3" /> Casting ✓ · drop
                          </button>
                        );
                      }
                      const full = (f.streamer_signups?.length || 0) >= 2;
                      return (
                        <button
                          onClick={() => signupToCast(f)}
                          disabled={full}
                          data-testid={`schedule-cast-signup-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-[#B084FF] border border-[#B084FF] hover:bg-[#B084FF] hover:text-black px-3 py-1.5 flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          <Radio className="w-3 h-3" /> {full ? "Cast full" : "Sign up to cast"}
                        </button>
                      );
                    })()}
                    {(f.streamer_signups?.length || 0) > 0 && (
                      <span
                        data-testid={`schedule-cast-count-${f.id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-gray-500 self-center"
                      >
                        {(f.streamer_signups || []).map((s) => s.handle).join(" · ")}
                      </span>
                    )}
                    {mySide(f) && !f.coin_toss && !editing && null}
                    {f.coin_toss && !f.faction_pick_locked && mySide(f) === f.coin_toss.winner && null}
                    {f.coin_toss && null}
                    {canEditFixture(f) && !editing && (
                      <button
                        onClick={() => startEdit(f)}
                        data-testid={`schedule-edit-${f.id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400 hover:bg-cyan-400 hover:text-black px-3 py-1.5"
                      >
                        Edit
                      </button>
                    )}
                    {isOfficial && !editing && (
                      <button
                        onClick={() => deleteFixture(f)}
                        data-testid={`schedule-delete-${f.id}`}
                        className="text-[10px] font-mono uppercase tracking-widest text-red-400 border border-red-500/40 hover:bg-red-500 hover:text-white hover:border-red-500 px-3 py-1.5"
                        title="Delete fixture (admin/official only)"
                      >
                        Delete
                      </button>
                    )}
                    {editing && (
                      <>
                        <button
                          onClick={() => saveEdit(f)}
                          data-testid={`schedule-save-${f.id}`}
                          className="text-[10px] font-mono uppercase tracking-widest text-black bg-cyan-400 hover:bg-cyan-300 px-3 py-1.5"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditRow(null)}
                          className="text-[10px] font-mono uppercase tracking-widest text-gray-500 hover:text-red-400 px-2 py-1.5"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </section>
          );
        })}
        {fixtures.length === 0 && (
          <div className="border border-[#232330] p-8 text-center text-gray-500">No fixtures scheduled.</div>
        )}
      </div>

      {/* Season generator dialog */}
      <Dialog open={seasonOpen} onOpenChange={setSeasonOpen}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight">Season Generator</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
              Strict per-division round-robin. Pick the divisions to draw. Each round runs one week apart. Odd divisions get a bye.
            </p>

            {/* Divisions multi-select */}
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400 flex items-center justify-between">
                Divisions to draw
                <span className="text-[10px] font-mono text-gray-500">{season.division_ids.length} selected</span>
              </Label>
              <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-[#232330] p-2 bg-[#05050A]" data-testid="season-division-picker">
                {divisions.length === 0 && (
                  <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 col-span-full p-2">No divisions yet — create one in Admin.</div>
                )}
                {divisions.map((d) => {
                  const on = season.division_ids.includes(d.id);
                  const teamCount = teams.filter((t) => t.division_id === d.id && (t.status === "approved" || !t.status)).length;
                  return (
                    <button
                      key={d.id}
                      type="button"
                      onClick={() => toggleSeasonDivision(d.id)}
                      data-testid={`season-div-${d.id}`}
                      className={`flex items-center justify-between text-left px-3 py-2 border transition-colors ${
                        on ? "border-cyan-400 bg-cyan-400/10 text-white" : "border-[#232330] text-gray-400 hover:border-white hover:text-white"
                      }`}
                    >
                      <span className="font-heading font-bold uppercase tracking-tight text-sm">{d.name}</span>
                      <span className={`text-[10px] font-mono uppercase tracking-widest ${teamCount < 2 ? "text-red-500" : "text-gray-500"}`}>
                        {teamCount} team{teamCount === 1 ? "" : "s"}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Preview */}
            {seasonRoundsPreview.length > 0 && (
              <div className="border border-[#F4D03F]/30 bg-[#F4D03F]/5 p-3" data-testid="season-preview">
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#F4D03F] mb-2">/ SCHEDULE PREVIEW</div>
                <div className="space-y-1">
                  {seasonRoundsPreview.map((p) => (
                    <div key={p.id} className="flex items-center justify-between text-xs font-mono" data-testid={`season-preview-${p.id}`}>
                      <span className="text-white uppercase tracking-widest">{p.name}</span>
                      {p.warn ? (
                        <span className="text-red-500">⚠ {p.warn}</span>
                      ) : (
                        <span className="text-gray-300">
                          {p.teams} teams · <span className="text-[#F4D03F] font-bold">{p.rounds} weeks{p.skip ? ` (R${p.skip + 1}–R${p.skip + p.rounds})` : ""}</span> · {p.matches} fixtures
                          {p.hasBye && <span className="text-gray-500"> · bye</span>}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">First kickoff (UTC)</Label>
              <Input
                data-testid="season-starts-at"
                type="datetime-local"
                value={season.starts_at}
                onChange={(e) => setSeason({ ...season, starts_at: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Days between rounds</Label>
              <Input
                data-testid="season-round-gap-days"
                type="number" min={1} max={30}
                value={season.round_gap_days}
                onChange={(e) => setSeason({ ...season, round_gap_days: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                7 = weekly · 14 = fortnightly · 1 = daily
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Default map</Label>
              <Input
                data-testid="season-default-map"
                value={season.default_map}
                onChange={(e) => setSeason({ ...season, default_map: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">Start from round #</Label>
              <Input
                data-testid="season-start-from-round"
                type="number" min={1} max={50}
                value={season.start_from_round}
                onChange={(e) => setSeason({ ...season, start_from_round: e.target.value })}
                className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-sm"
              />
              <div className="text-[10px] font-mono uppercase tracking-widest text-gray-500 mt-1">
                1 = full season · 2 = skip R1 (already set), generate R2 onwards · First kickoff below applies to whichever round you start from
              </div>
              {suggestedStartFrom > 1 && Number(season.start_from_round) !== suggestedStartFrom && (
                <button
                  type="button"
                  onClick={() => setSeason({ ...season, start_from_round: suggestedStartFrom })}
                  data-testid="season-start-from-round-suggest"
                  className="mt-2 text-[10px] font-mono uppercase tracking-widest text-cyan-400 border border-cyan-400/40 hover:border-cyan-400 hover:bg-cyan-400/10 px-2 py-1 inline-flex items-center gap-2 transition-colors"
                  title={`Existing fixtures for the selected division(s) go up to R${suggestedStartFrom - 1}. Click to auto-fill the next round.`}
                >
                  🔍 Detected R{suggestedStartFrom - 1} already scheduled — Use R{suggestedStartFrom}
                </button>
              )}
            </div>
            <label className="flex items-center gap-2 text-xs text-gray-300">
              <input
                type="checkbox"
                data-testid="season-home-away"
                checked={season.home_away}
                onChange={(e) => setSeason({ ...season, home_away: e.target.checked })}
              />
              Home + away (each pair plays twice — doubles the season)
            </label>
            <label className="flex items-center gap-2 text-xs text-gray-300">
              <input
                type="checkbox"
                data-testid="season-announce-discord"
                checked={season.announce_discord}
                onChange={(e) => setSeason({ ...season, announce_discord: e.target.checked })}
              />
              Announce on Discord (each team gets its slate in their private channel)
            </label>
            <Button
              onClick={generateSeason}
              disabled={season.division_ids.length === 0}
              data-testid="season-generate-submit"
              className="w-full rounded-none bg-[#B084FF] hover:bg-purple-400 text-black uppercase tracking-widest font-bold disabled:opacity-50"
            >
              <Sparkles className="w-4 h-4 mr-2" /> Draw the season
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Iter30: Predict dialog on Schedule */}
      <Dialog open={!!predictFor} onOpenChange={(o) => !o && setPredictFor(null)}>
        <DialogContent className="bg-[#101015] border-[#232330] text-white rounded-none" data-testid="schedule-predict-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2">
              <Target className="w-4 h-4 text-cyan-400" /> Predict · {teamById(predictFor?.home_team_id)?.tag} vs {teamById(predictFor?.away_team_id)?.tag}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 pt-2">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{teamById(predictFor?.home_team_id)?.tag}</Label>
              <Input type="number" min={0} value={ph} onChange={(e) => setPh(e.target.value)} data-testid="schedule-predict-home" className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2 h-16 text-center" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-400">{teamById(predictFor?.away_team_id)?.tag}</Label>
              <Input type="number" min={0} value={pa} onChange={(e) => setPa(e.target.value)} data-testid="schedule-predict-away" className="rounded-none bg-[#05050A] border-[#232330] font-mono text-2xl mt-2 h-16 text-center" />
            </div>
          </div>
          <Button onClick={submitPrediction} data-testid="schedule-confirm-predict" className="w-full rounded-none bg-cyan-400 hover:bg-cyan-300 text-black uppercase tracking-widest font-bold mt-2">
            Lock prediction
          </Button>
        </DialogContent>
      </Dialog>

      {/* Iter28: Submit result dialog (reps) with RCON auto-import */}
      <Dialog open={!!scoreFor} onOpenChange={(o) => !o && setScoreFor(null)}>
        <DialogContent className="bg-[#101015] border-[#F4D03F] text-white rounded-none" data-testid="schedule-score-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-tight flex items-center gap-2 text-[#F4D03F]">
              <Trophy className="w-4 h-4" /> Report final score
            </DialogTitle>
          </DialogHeader>
          {scoreFor && (
            <div className="space-y-3 text-sm">
              <p className="text-[10px] font-mono uppercase tracking-widest text-gray-500">
                Both team reps must submit matching scores before standings lock. Officials can override any dispute.
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-cyan-400">{teamById(scoreFor.home_team_id)?.tag}</Label>
                  <Input type="number" min={0} value={sh} onChange={(e) => setSh(e.target.value)} data-testid="schedule-score-home" className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center" />
                </div>
                <div>
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-[#FF007F]">{teamById(scoreFor.away_team_id)?.tag}</Label>
                  <Input type="number" min={0} value={sa} onChange={(e) => setSa(e.target.value)} data-testid="schedule-score-away" className="rounded-none bg-[#05050A] border-[#232330] mt-2 font-mono text-2xl h-16 text-center" />
                </div>
              </div>
              <div>
                <Label className="text-[10px] font-mono uppercase tracking-widest text-gray-500">RCON auto-import player stats</Label>
                <div className="grid grid-cols-3 gap-2 mt-2" data-testid="schedule-pull-mode-group">
                  {[
                    { v: "off", label: "Off", color: "#232330" },
                    { v: "rcon", label: "Live RCON", color: "#00FFB2" },
                    { v: "csv", label: "Paste CSV", color: "#F4D03F" },
                  ].map((opt) => (
                    <button
                      key={opt.v}
                      type="button"
                      onClick={() => setPullMode(opt.v)}
                      data-testid={`schedule-pull-mode-${opt.v}`}
                      className="border py-2 text-[10px] font-mono uppercase tracking-widest transition-colors"
                      style={pullMode === opt.v ? { background: opt.color, borderColor: opt.color, color: "black", fontWeight: "bold" } : { borderColor: "#232330", color: "#9ca3af" }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
                {pullMode === "rcon" && (
                  <div className="mt-2 space-y-2">
                    <Input
                      value={rconLink}
                      onChange={(e) => setRconLink(e.target.value)}
                      placeholder="Optional: paste RCON web link (e.g. https://rcon.legacy.gg/match/xxxx)"
                      data-testid="schedule-rcon-link"
                      className="rounded-none bg-[#05050A] border-[#00FFB2]/40 font-mono text-xs"
                    />
                    <p className="text-[10px] font-mono uppercase tracking-widest text-[#00FFB2]" data-testid="schedule-rcon-note">
                      Live pull uses HLL_RCON_HOST / PORT / PASSWORD from server env. Auto-falls back to simulated when unreachable.
                    </p>
                  </div>
                )}
                {pullMode === "csv" && (
                  <Textarea
                    value={rconCsv}
                    onChange={(e) => setRconCsv(e.target.value)}
                    placeholder={"handle,kills,deaths,assists,mvp\nGhostFoxx,32,14,6,1"}
                    data-testid="schedule-rcon-csv"
                    className="mt-2 rounded-none bg-[#05050A] border-[#232330] font-mono text-xs min-h-24"
                  />
                )}
              </div>
              <Button onClick={submitScore} data-testid="schedule-score-submit" className="w-full rounded-none bg-[#F4D03F] hover:bg-yellow-300 text-black uppercase tracking-widest font-bold">
                <Save className="w-4 h-4 mr-2" /> Submit result
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
